import axios from 'axios';
import { sharedGet } from './sharedGet';
import { apiUrl, IS_DEMO } from '../../apiBase';
import { colourwayQuery } from './colourwayPaint';
import type { Face } from './faces';
import type { PictureRef, PictureSource } from './pictureRef';
import type { Extent } from './meshAnnotations';

// The half of /api/notice/3d that every model page shares: the status probe, the run
// history, deleting a run, and fetching a mesh. What a model is CALLED with differs and
// lives in that model's own module (openaiBlenderApi); everything here is the same
// whichever model made the row.

// One recorded run, whichever model made it. `ready` is the only thing worth branching
// on: a run with no mesh on disk is either a failure (`error` says why) or one whose
// file went missing, and neither can be shown.
//
// ON SCREEN IT IS CALLED A MESH, and this code goes on calling it a run because that is
// what the table is (notice_3d_jobs, one row per attempt, kept whether it worked or not).
// The manual has runs of its own — a pass over its phases, numbered 1 to 12 in the bar at
// the top of the viewer — and the two are unrelated: a mesh outlives every later run of
// the notice it was made in. Two numbered things called the same word on one page is one
// too many, so the word on screen is the geometry, which is what somebody is looking at.
// WHERE A RUN CAME FROM: one of the three picture tables, or SUBSTEP — the piece a sub-step
// leaves, signed off in Phase 5's viewer rather than reconstructed from a photograph, which has
// no input picture at all. See saveSubstepOutput.
export type RunSource = PictureSource | 'substep';

export type Job3D = {
  id: number;
  // The fal endpoint id that produced this run — the pages filter on it, so each
  // model's history is its own.
  model: string;
  source: RunSource;
  imageId: number | null;
  itemId: number | null;
  params: Record<string, unknown>;
  // One entry per extra output a run produced: a segmented object's mesh, or one mask.
  // Empty for a single-output run. `score` is how sure the segmenter was, and only the
  // 2D endpoint reports it.
  parts: { i: number; bytes: number; score?: number }[];
  sizeBytes: number | null;
  elapsedSeconds: number | null;
  error: string | null;
  ready: boolean;
  // This run's mesh is the part's 3D model in the Parts DB — the curated subset Phase 6
  // assembles from. Every run is kept; this is the one somebody chose.
  inCatalog: boolean;
  // WHICH SHAPE THE MESH IS — the mark a sign-off keeps of it (see fittingModels.partMark), read
  // off the file by the server. Null on a run that is not a part's kept model. `shapeSince` is
  // for the marks written before it: the count they would have stood at when the mesh became
  // the shape it is now.
  shape?: string | null;
  shapeSince?: number | null;
  createdAt: string | null;
};

// Whether the server can run anything at all, and what each model charges. Asked before
// a button is offered: "no key configured" is a different thing to tell someone than a
// run that failed.
export type Status3D = {
  // THE ENGINE THAT WRITES RATHER THAN RECONSTRUCTS: an OpenAI model hands back a bpy
  // script and the server's Blender runs it. Its own `configured`, because it is its own
  // key — a server can have fal's and not this one. No prices: it is billed on tokens nobody can count before the call.
  openai: {
    configured: boolean;
    // `efforts` is what each model's reasoning may be set to, `defaultEffort` what it runs at
    // when nobody says — see openai_blender.MODELS.
    models: { id: string; label: string; efforts?: string[]; defaultEffort?: string }[];
    // Models no longer offered whose runs are still in the histories — named, fetched, never run.
    retired?: { id: string; label: string }[];
    defaultModel: string;
    // HOW MANY TIMES IT MAY MODEL THE THING, and how many it does when nobody says. The
    // route clamps whatever arrives to this list, so the dropdown offers the server's
    // answer rather than a copy of it — see openai_blender.PASS_CHOICES.
    passes?: number[];
    defaultPasses?: number;
  };
};

// The combined scene of a run — and, for a run that segmented, one object of it.
//
// `version` is a CACHE BUSTER and nothing else: the server ignores it. A repair now
// overwrites the file behind this URL, so without it the viewer would re-request an
// address it already has and be handed the mesh from before the repair — the one visible
// symptom of which is a button that appears to do nothing.
// ALWAYS STAMPED, 0 INCLUDED. The stamp is the edit count, and it was left off when there
// were no edits — which made the unedited mesh, the common case, the one URL the server could
// not cache hard: bytes it would have to revalidate on every open because a repair could
// overwrite them. Stamped, the address IS the version, so the server answers it immutable and
// a repair asks a different question rather than changing this one's answer. See _send_asset.
//
// AND IN THE COLOURWAY ON SCREEN, with its paint's stamp (see colourwayPaint): the server paints a
// product's shape in it, and sends any other run as it is.
//
// AND PACKED (backend/glbpack.py): Meshopt geometry, KTX2 textures, shared copies stored once —
// a tenth of the bytes, often a hundredth. Only the page's own loader reads that (see
// meshLoader), so a file meant to leave the page — the download button — asks with `pack` false.
// WHICH PACKING A PACKED MESH IS ASKED IN: the server's glbpack.PACKER, raised with it. In the
// address because a browser keeps a mesh it was sent (an hour, or for good at a stamped address):
// the same address after a packing change was the old file, hardware's 1024 px maps still in it
// (Louis 2026-10-26). A new number is a new address. The server packs whatever was asked.
export const PACKED = 5;

export const meshSrc = (jobId: number, version = 0, pack = true) =>
  apiUrl(`/api/notice/3d/model/${jobId}?v=${version}${colourwayQuery()}${pack ? `&pack=${PACKED}` : ''}`);
// THE SAME MESH, NOT PACKED, TO SAVE AS `name`: a GLB any viewer opens (Blender, a browser's), its
// textures and the colourway on screen in it, stood Y up. The packed one needs this page's own
// decoders. The server names it (see get_model): the file's own name beat the page's `download`.
// `explode`, for a model with an exploded view: that view as the file's animation, the same spread
// (see glb.downloadable).
export const downloadSrc = (src: string, name: string, explode?: 'centre' | 'z') =>
  `${src.replace(/&pack=\d+/, '')}&name=${encodeURIComponent(`${name.replace(/[\\/:*?"<>|]+/g, ' ').trim()}.glb`)}${
    explode ? `&explode=${explode}` : ''}`;

// The mesh as it stood after `at` edits — 0 being as it was made, and the edit count being
// as it stands. A LOOK BACK and not a walk back: every overwrite already copies the mesh
// aside, so each of these is a file that has been on disk all along, and asking for one
// changes nothing about the run.
//
// STAMPED WITH THE RUN'S VERSION where the caller has it (see meshStamp): a change taken off the
// history makes every state after it again under the same count, so the count alone does not name
// the bytes. Stamped, the browser keeps it for good; unstamped (`fresh`), it asks each time. And
// IN THE COLOURWAY ON SCREEN, as meshSrc is: drawn beside parts that are.
export const meshAtSrc = (jobId: number, at: number, stamp?: number) =>
  apiUrl(`/api/notice/3d/model/${jobId}/at/${at}?${stamp != null ? `v=${stamp}` : 'fresh=1'}${colourwayQuery()}`);

export const partSrc = (jobId: number, index: number) =>
  apiUrl(`/api/notice/3d/model/${jobId}/part/${index}`);

// What has been done to a run's mesh since it was made, oldest first. Empty for a mesh
// nobody has touched, which is most of them.
//
// ONE STACK for all of it, whether the thing done was a boolean or a scale factor. They
// are all the answer to "what has been done to this", and two stacks would be two answers
// to one question — as well as two sets of undo files to keep in step.
// ONE BRIGHT PATCH A RETOUCH FLATTENED, and how hard — `box` is [x0,y0,x1,y1] in 0..1 of the
// picture. Only read back now, off the edits of meshes retouched before the panel went.
export type RetouchSpot = { box: [number, number, number, number]; strength: number };

export type MeshEdit = {
  // 'lathe' is a re-turn: the same run, rebuilt from new measurements. It sits in this
  // stack with the rest because it is one more thing done to the mesh, and undoing it has
  // to walk back through the others in the order they happened.
  // 'texture' is a SITTING of corrections in the retouch panel, applied one at a time in
  // the browser and written once — where 'retouch' and 'level' are the single-press
  // ancestors of it, still on meshes edited before that changed.
  // 'orient' is a rigid turn: the mesh re-authored into the finished product's frame, so
  // the six face names the assembly phase writes on its box are true of the furniture
  // rather than of the picture it was reconstructed from. See mesh_orient.py.
  // 'drill' is a hole bored into one face — the thing a reconstruction is always missing,
  // said as a place on that face in millimetres. See drillJob.
  // 'close' makes the mesh the boundary of a solid: degenerates dissolved, specks dropped,
  // tears capped, winding turned outward. Still named on meshes edited before it was removed.
  // 'fill_cut' and 'fill_cap' are the drill run backwards, in the two presses it takes: a
  // capsule out of the whole bore, and then every wound that leaves capped on the part's own
  // triangle size and wearing its own texture. The step before boring the ones the drawing
  // specifies. 'fill' is what the single press left behind, and 'fill_close' what the closing
  // step was called before it was rebuilt; both are still on rails written earlier.
  // 'fill_all' is one press of Cut & fill all, folded into one entry: a hole costs two edits
  // and a table of four costs eight, and what somebody DID was press one button. `holes` says
  // how many, and one Undo of it reaches the mesh from before the first cut of the run.
  // 'grain' is a surface laid on a mesh that had none: unwrapped, and a photograph of the
  // real wood tiled over it. Written meshes made before materials were stated were born with
  // theirs, laid as the run was saved, so nothing makes one of these any more — the word stays because the runs that carry one
  // still have to be named in the rail.
  // 'drill_all' is one batch of bores, cut in one Blender run by the Holes & covers run (see
  // drillJobAll): one entry however many, each bore kept in `bores`, and `holes` says how many.
  op: RepairOp | 'rescale' | 'script' | 'colour' | 'grain' | 'lathe' | 'retouch' | 'level' | 'texture'
    | 'orient' | 'drill' | 'drill_all' | 'close' | 'keep' | 'fill' | 'fill_cut' | 'fill_close' | 'fill_cap'
    | 'fill_all' | 'pieces' | 'materials';
  // 'materials' is a materials reading laid on the mesh's own materials (see apply_materials):
  // paint and grain, and not a point of it moved.
  // drill_all only: every bore it cut, as a 'drill' entry says one.
  bores?: { id?: string; face: string; u: number; v: number; diameter: number; depth: number | null; through: boolean }[];
  at: string;
  // Pieces only: which pieces of a whole product were put on which material — see
  // followPartMaterials.
  pieces?: { piece: string; material: string }[];
  // Drills only: which face it went into, where on it, how wide, how deep — millimetres,
  // as they were typed. `depth` is absent on a hole that goes right through.
  face?: string;
  u?: number;
  v?: number;
  // fill_all only: how many holes that one press cut and filled.
  holes?: number;
  diameter?: number;
  depth?: number | null;
  through?: boolean;
  // Keeps only: how many pieces of a mesh in several were kept, and how many deleted.
  kept?: number;
  // Fills only: how many refining passes the patch was given, where somebody said.
  passes?: number;
  // Closings only: how many tears were capped, how many stray shells went, and whether the
  // mesh ended up closed. "Closed the surface" on an attempt that did not is a rail entry
  // that says the opposite of what happened.
  filled?: number;
  dropped?: number;
  closed?: boolean;
  // Repairs only: which end was worked on, and with what.
  end?: 'min' | 'max';
  style?: string;
  widthMm?: number;
  lengthMm?: number;
  depthMm?: number;
  // Scripts only: what it was called, and the code that ran. The code is copied ONTO the
  // edit, so an undo can replay it without anything else being kept.
  name?: string;
  code?: string;
  // The proposal it came from — none on scripts run off the old library menu. What the
  // history graph draws its trunk from.
  proposalId?: number;
  instruction?: string;
  // Texture sittings only: how many of each kind of correction went into the one entry.
  // `marks` rather than `spots` because that one already means the LIST of rectangles on the
  // older per-press op, and a field carrying a list here and a count there is a field every
  // reader has to narrow before it can use.
  faces?: number;
  marks?: number;
  // Rescales only: which axis was fitted, and to what the drawing said.
  axis?: 'x' | 'y' | 'z' | null;
  mm?: number | null;
  // Orientations only: which face of the mesh ended up as the product's front and as its top,
  // named as the mesh called them BEFORE the turn, and the quaternion derived from them. The
  // quaternion is what replays the edit when an earlier one is dropped out from under it — the
  // two words are what a person reads back to judge whether the turn was right.
  front?: OrientFace;
  top?: OrientFace;
  // And the answer in its own words — which lettered view shows which side, and whether the
  // turn that was applied agrees — which is what the badge prints. See OrientAnswer.
  views?: OrientView[];
  // Whether all six agreed. Only an applied turn is recorded, so there is no `settled` here:
  // an answer that named no turn wrote no edit for this to sit on.
  coherent?: boolean;
  // TURNED BY HAND rather than by a model: the same two faces above and the same rotation
  // under them, without the reasoning, the raw reply or the bill. Consecutive ones fold into a
  // single entry on the server, and `front`/`top` are recomputed from the folded rotation — so
  // they name the faces of the mesh as it was before the FIRST of them, which is the only
  // frame a reader has left to read them in.
  hand?: boolean;
  rotation?: [number, number, number, number];
  reason?: string;
  // And the evidence around the answer — what it replied, which model, and where the wait
  // went. Kept on the edit so the badge reads them back off the run days later rather than
  // asking again. See OrientAnswer, whose fields these are.
  raw?: string;
  model?: string;
  renderSeconds?: number;
  modelSeconds?: number;
  // Colours only: the sRGB it was painted, and the photograph that was sampled for it.
  // Both, because three digits with no provenance are three digits nobody can argue with.
  color?: [number, number, number];
  // WHAT it is, when a material was named instead of a pixel sampled, and the two numbers
  // that came with the name. The rail says the name: "felt, grey" is a claim somebody can
  // disagree with, where three digits are not.
  material?: string | null;
  roughness?: number | null;
  metallic?: number | null;
  // And the surface drawn over the colour, where one was — a default material's texture.
  texture?: { contrast: number; chroma: number; anisotropy: number; periodMm: number };
  // Whether the mesh's own grain was kept under it, as it HAPPENED and not as it was asked
  // for: a part with no base-colour texture has no grain to keep, so this comes back false
  // however the button was pressed. Absent on every colour applied before the choice existed.
  grain?: boolean | null;
  // The same distinction one surface along: `fibre` is what the mesh took, `wantedFibre`
  // what the material asked for. They differ on a mesh shaped before the lathe unwrapped
  // what it makes, which has nowhere to lay a fibre — and the difference is what the panel
  // reads to say "re-shape it" rather than leaving a felt pad silently plastic.
  fibre?: boolean | null;
  wantedFibre?: boolean | null;
  // Retouches only: the rectangles that were flattened and how hard. Kept so a correction
  // can still be judged afterwards — the picture no longer shows where it was applied.
  spots?: RetouchSpot[] | null;
  from?: number | null;
  // Re-turns only. `what` is the change in words — "head across 14 mm" — built on the
  // server beside the field names it is made of, because a copy of those here would drift
  // the first time a profile gained a dimension and would drift silently.
  //
  // `was` is what the run measured before it, and `until` the last nudge folded into it: an
  // entry covers a whole sitting of adjustments, not one keystroke, so it has a beginning
  // (`at`) and an end.
  what?: string;
  was?: Record<string, number> | null;
  until?: string;
};

export const editsOf = (job: Job3D | null | undefined): MeshEdit[] =>
  (job?.params?.edits as MeshEdit[] | undefined) ?? [];

// THE EDITS THAT LEAVE A PART'S SHAPE WHERE IT WAS — its surface, repainted or regrained, or given
// its materials. Every point of the mesh stands where it stood.
export const SURFACE_EDITS: MeshEdit['op'][] = ['colour', 'grain', 'retouch', 'level', 'texture', 'pieces', 'materials'];

// The fills that close a bore, each a hole taken back out of a part.
export const BORE_FILLS: string[] = ['fill', 'fill_close', 'fill_cap', 'fill_cut', 'fill_all'];

// THE MESH AS IT STOOD BEFORE ITS FIRST BORE, as a count of edits for meshAtSrc — what a Holes &
// covers run works on, since it undrills a joint's parts before asking (see drillParts), and what
// the air walk measures a line on (see holeAir), a bore's own walls being no wood ending. The edit
// count where it was never drilled; null where its SHAPE was changed after a bore, so no saved
// state is the undrilled one.
//
// A REPAINT AFTER THE BORES IS NO REASON. Its shape is the one before them, which is what is asked
// for; only its paint is older. It was a reason, and on 16 Oct the colourway's materials, written
// into every component as an edit after its bores, put every part's drilled mesh into the air walk:
// each line ran down its own bore and called it 27 mm of open air.
//
// NOR A PLUG (Louis 2026-10-29): a fill after the bores closes one of them, so the shape before the
// first bore is still the one asked for. Plugging the pointed screws' holes did the same as the
// repaint did, H101-B down its own bore in the rail.
export function undrilledAt(job: Job3D | null | undefined): number | null {
  const edits = editsOf(job);
  const drill = (e: MeshEdit) => e.op === 'drill' || e.op === 'drill_all';
  const first = edits.findIndex(drill);
  if (first < 0) return edits.length;
  return edits.slice(first).every((e) => drill(e) || BORE_FILLS.includes(e.op) || SURFACE_EDITS.includes(e.op))
    ? first : null;
}

// WHICH STATE OF THE LIVE MESH A VIEWER IS LOOKING AT, for the cache buster on its address.
//
// The live mesh has ONE URL and every edit overwrites what is behind it, so the number on
// the end of that URL is the only thing telling a viewer to fetch again. It was the edit
// COUNT, which is right until an edit folds into the one before it — nudging a measurement
// files the whole run of nudges as one entry, deliberately, so the second nudge changed the
// file on disk and nothing about the address. The mesh in the modal stayed at the first
// nudge's shape while the fields above it read the latest, and closing the modal was the
// only way to see what had actually been made.
//
// `version` counts WRITES and is what the server moves on each one. The count stands in for
// runs saved before it existed, which is the value the address already carried for them.
export const meshStamp = (job: Job3D | null | undefined): number =>
  (job?.params?.version as number | undefined) ?? editsOf(job).length;

export async function fetchStatus3D(): Promise<Status3D> {
  const { data } = await axios.get<Status3D>('/api/notice/3d/status');
  return data;
}

// One model's runs, newest first. Always scoped to a model: comparing a written run
// against a SAM run is not what either page is for.
export async function fetchJobs(model: string | string[]): Promise<Job3D[]> {
  // SEVERAL models is one list, not several: a part's history is its written runs under
  // every model and its older uploads, in date order. Sent as a repeated
  // `model` — axios would spell an array `model[]=`, which Flask's getlist does not read.
  const params = new URLSearchParams(
    (Array.isArray(model) ? model : [model]).map((m) => ['model', m]),
  );
  const { data } = await axios.get<{ jobs: Job3D[] }>('/api/notice/3d/jobs', { params });
  return data.jobs || [];
}

// ONE NOTICE'S WHOLE-PRODUCT RUNS, whichever engine made them — asked by the mark in
// their params rather than by the model, which is the only thing the two ways of making
// one have in common. See productRunsOf for the submodel half of the same filter, and
// list_jobs, where the two filters are alternatives.
// EVERY NAMED OBJECT OF A MODEL PAINTED ITS PART'S MATERIAL — what the build applies, applied to
// a model already built (see glb.painted_parts), and to the pieces already cut from it. For a
// hardware set built before that step existed, or from colours that have changed since.
export async function paintParts(
  jobId: number, paint: { part: string; material: unknown }[],
): Promise<{ job: Job3D; painted: string[]; pieces: number }> {
  const { data } = await axios.post<{ job: Job3D; painted: string[]; pieces: number }>(
    `/api/notice/3d/jobs/${jobId}/paint-parts`, { paint },
  );
  return data;
}

// THIS MANUAL'S HARDWARE SETS — runs building several pieces of hardware together, one row of
// Phase 4's hardware table each. See _set_mark on the server.
export async function fetchSetJobs(docId: string): Promise<Job3D[]> {
  const { data } = await sharedGet<{ jobs: Job3D[] }>('/api/notice/3d/jobs', {
    params: { set: docId },
  });
  return data.jobs || [];
}

// Which set a run builds, by its key — the parts' catalog ids, sorted and joined.
export const setKeyOf = (job: Job3D): string | null =>
  ((job.params?.set as { key?: string } | undefined)?.key ?? null);

// SHARED, as the lists below are: every reader of this manual's runs asks at once (see sharedGet).
export async function fetchProductJobs(docId: string): Promise<Job3D[]> {
  const { data } = await sharedGet<{ jobs: Job3D[] }>('/api/notice/3d/jobs', {
    params: { product: docId },
  });
  return data.jobs || [];
}

// WHAT A SUB-STEP MAKES, saved as a run of its own — the output of a movement somebody has
// watched and signed off in Phase 5.
//
// A LIST AND NOT A MESH: the parts that were on screen, each by its run and by where it stood.
// It was one GLB baked out of the viewer, which carried every part's geometry again — and the
// piece made from that piece carried it a third time. The list is a few dozen numbers, and a
// scene drawing the piece downloads each part once (see ScenePart.pieces). Anything that wants
// one file is still handed one: /3d/model/<id> puts the parts together.
//
// Filed under the sub-step's own key, which is what the fitting is stored under: the piece and
// the sign-off that produced it are one identity, and re-validating writes another row rather
// than editing this one — the newest is the answer, as it is for a part's runs.
export type SubstepOutput = { docId: string; slot: string; label: string };

// One part of a piece. `m` is where it stands — its transform in the assembly's own frame, in
// millimetres, sixteen numbers column-major (a Matrix4's `elements`). `mark` is which model of
// the part that was (see modelMark): a piece whose part has since become another model is not
// the piece that was signed off. Null on a list nobody can vouch for either way.
export type PiecePart = { job: number; m: number[]; name: string; mark: string | null };

// What this run is made of, where it is a sub-step's piece kept as a list. Null for a run that
// is its own mesh — every part, and a piece baked before pieces were listed.
export const madeOf = (job: Job3D | null | undefined): PiecePart[] | null => {
  const said = (job?.params?.substep as { pieces?: PiecePart[] } | undefined)?.pieces;
  return Array.isArray(said) && said.length ? said : null;
};

export async function saveSubstepOutput(
  what: SubstepOutput, pieces: PiecePart[],
): Promise<Job3D | null> {
  const { data } = await axios.post<{ job?: Job3D }>('/api/notice/3d/substep', { ...what, pieces });
  return data.job ?? null;
}

// EVERY SUB-STEP OUTPUT THIS MANUAL HAS, newest first — which is the order the list arrives in,
// so the first entry for a slot is that slot's current mesh.
export function substepOutputsOf(jobs: Job3D[], docId: string): Map<string, Job3D> {
  const out = new Map<string, Job3D>();
  for (const job of jobs) {
    const said = (job.params?.substep ?? null) as { docId?: string; slot?: string } | null;
    if (!job.ready || job.source !== 'substep' || said?.docId !== docId) continue;
    if (said.slot && !out.has(said.slot)) out.set(said.slot, job);
  }
  return out;
}

// THE WHOLE PRODUCT, reconstructed in one piece — Phase 5's first segment.
//
// A run made from a LISTING photograph rather than from a part's, and so with no catalog
// item behind it: the chair is not a row of the Parts DB, it is what the Parts DB's rows
// add up to. Nothing on the row can find it again, so the run says what it is OF in its
// params — exactly as a baked sub-step does: the document, and the submodel whose
// photographs it was made from. A blue chair and a grey one are two products here, made
// from two sets of pictures, and one list holding both would show a run of neither.
export type ProductMark = { docId: string; submodel: string };

export const productMarkOf = (job: Job3D): ProductMark | null => {
  const said = (job.params?.product ?? null) as Partial<ProductMark> | null;
  if (!said || typeof said.docId !== 'string' || !said.docId) return null;
  // A submodel of '' is the notice with one listing, which is a real answer and not a
  // missing one — so it is defaulted rather than refused.
  return { docId: said.docId, submodel: typeof said.submodel === 'string' ? said.submodel : '' };
};

// This notice's whole-product runs for one submodel, newest first (the order the route
// answers in). Failures included, as the part panel keeps them: a run that was billed and
// produced nothing is still something that happened, and something to delete.
//
// AND THE ONE KEPT, WHICHEVER COLOURWAY BUILT IT: a product is one shape, every colourway's, painted
// in the one on screen (see colourwayPaint). Not the copies each colourway was once given of it.
export function productRunsOf(jobs: Job3D[], docId: string, submodel: string): Job3D[] {
  return jobs.filter((job) => {
    const mark = productMarkOf(job);
    return !!mark && mark.docId === docId && !job.params?.copiedFrom
      && (mark.submodel === submodel || job.inCatalog);
  });
}

// A WHOLE PRODUCT MODELLED ELSEWHERE, brought in as a run of the product (see import_product):
// the file first, then the part each of its objects is (nameImport). `objects` are what the
// file holds once flattened, named as the file named them, in millimetres.
export const IMPORT_MODEL = 'import';
export type ImportedObject = { name: string; sizeMm: number[]; centreMm: number[] };
// `whole`: the file did not separate its parts, and one object of it was kept as the whole product.
export type ImportMark = {
  filename: string; scale: number | null; objects: ImportedObject[]; names: Record<string, string>;
  whole: boolean;
};

export const importOf = (job: Job3D | null | undefined): ImportMark | null => {
  if (job?.model !== IMPORT_MODEL) return null;
  const said = (job.params?.imported ?? {}) as Partial<ImportMark>;
  return {
    filename: typeof said.filename === 'string' ? said.filename : '',
    scale: typeof said.scale === 'number' ? said.scale : null,
    objects: Array.isArray(said.objects) ? said.objects : [],
    names: said.names && typeof said.names === 'object' ? said.names : {},
    whole: said.whole === true,
  };
};

export async function importProduct(file: File, docId: string, submodel: string): Promise<Job3D> {
  const form = new FormData();
  form.append('file', file);
  form.append('docId', docId);
  form.append('submodel', submodel);
  const { data } = await axios.post<{ job: Job3D }>('/api/notice/3d/import', form);
  return data.job;
}

// Either the part each object is, or the objects that are each the whole product (see name_import).
export async function nameImport(
  jobId: number, body: { names: Record<string, string> } | { whole: string[] },
): Promise<Job3D> {
  const { data } = await axios.put<{ job: Job3D }>(`/api/notice/3d/import/${jobId}/names`, body);
  return data.job;
}

export async function deleteJob(jobId: number): Promise<void> {
  await axios.delete(`/api/notice/3d/jobs/${jobId}`);
}

// ONE COMPONENT OF A WHOLE PRODUCT, KEPT AS THE PART'S OWN MODEL — what the mark on a run
// cut out of another one says. `of` is the run it came from, `name` what the model called the
// mesh ("Side rail (Left)") and `part` what the parts list calls the part.
//
// See _keep_pieces, which mints these, and runNumberOf, which is why they exist on the page:
// a piece is shown under the number of the run it was cut from, not its own.
//
// `from` is what the run it was cut from is a model of: the whole product, or a hardware set.
// Null on a piece cut before that was recorded — see fix_piece_measures.py, which fills it in.
//
// `tool`: cut from a tool's set, the tool and the hardware it drives (see toolSet) — the model a
// joint's set reuses rather than builds again.
export type PieceMark = {
  of: number; name: string; part: string; docId: string; submodel: string;
  from: 'product' | 'set' | null; tool: boolean;
};

export const pieceOf = (job: Job3D | null | undefined): PieceMark | null => {
  const said = (job?.params?.piece ?? null) as Partial<PieceMark> | null;
  if (!said || typeof said.of !== 'number') return null;
  return {
    of: said.of,
    name: typeof said.name === 'string' ? said.name : '',
    part: typeof said.part === 'string' ? said.part : '',
    docId: typeof said.docId === 'string' ? said.docId : '',
    submodel: typeof said.submodel === 'string' ? said.submodel : '',
    from: said.from === 'product' || said.from === 'set' ? said.from : null,
    tool: said.tool === true,
  };
};

// THE NUMBER A RUN IS KNOWN BY. Its own id, or — for a component cut out of a whole
// product — the number of the run it was cut from.
//
// Six meshes that came out of one press are ONE answer, and numbering them 155 to 160 would
// present them as six runs nobody remembers making. Under the product's number, the seat in
// the part's strip and the chair in the product's strip are visibly the same model, which is
// exactly what they are.
export const runNumberOf = (job: Job3D): number => pieceOf(job)?.of ?? job.id;

// THE PART EACH COMPONENT OF A WRITTEN PRODUCT IS, for the press that keeps them: the name
// the model was told, and the catalog row the page matched it to (see partIdentity).
export type PieceItem = { name: string; itemId: number };

// Keep a run's mesh as a 3D model of its part or product, or stop keeping it. Answers the
// updated row, so the caller shows what the server stored rather than what it hoped for.
//
// SEVERAL MAY BE KEPT — see set_in_catalog, where the rule for which one the add-on uses is.
//
// `parts` is what a WHOLE PRODUCT'S components are: given, the server cuts the mesh up and
// keeps each piece as its part's model, and answers which ones it could not place. A part's
// own run has none to send.
export async function setJobInCatalog(
  jobId: number, inCatalog: boolean, parts?: PieceItem[],
): Promise<{ job: Job3D; pieces: KeptPieces | null }> {
  const { data } = await axios.put<{ job: Job3D; pieces?: KeptPieces }>(
    `/api/notice/3d/jobs/${jobId}/catalog`,
    { inCatalog, ...(parts?.length ? { parts } : {}) },
  );
  return { job: data.job, pieces: data.pieces ?? null };
}

// What that press did with the components: the pieces kept, and the ones named by no part of
// this manual's list — which is said out loud rather than swallowed, a four-of-five answer
// being a parts list and a mesh that disagree about what the product is made of.
export type KeptPieces = {
  kept: { name: string; itemId: number; jobId: number }[];
  skipped: string[];
  // HOW MANY THE PRESS TOOK BACK OUT — the components of the run as it was kept BEFORE this
  // press, which go on every press: a switch turned off takes the parts' models with it, and
  // a switch turned on again cuts them fresh rather than leaving yesterday's beside today's.
  forgot: number;
  error?: string;
};

// This run's mesh, resized, IN PLACE and undoably. The scale is in the file's own axes
// and is computed by the viewer that measured it — see axisFit in meshAnnotations.
//
// It made a new run once, like a repair did, and stopped for the same reason: the strip
// filled with the same mesh at three sizes and the corrected one sat outside the Parts DB
// until somebody flipped a switch. It joins the same edit stack, so dropEdit takes a
// resize back exactly as it takes back a boolean.
export async function rescaleJob(
  jobId: number,
  body: { scale: [number, number, number]; axis: 'x' | 'y' | 'z'; mm: number },
): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(
    `/api/notice/3d/jobs/${jobId}/rescale`,
    body,
  );
  return data.job;
}

// One flat colour on this run's mesh, in place and undoably — the fix for a converted CAD
// file, whose geometry is exact and whose material is a placeholder nobody chose.
//
// `rgb` is sRGB 0-255, sampled from a photograph by the page (see sampleColour): the
// picture is already on screen there, and what gets approved is what was measured. `from`
// is the photo it came from, kept as the provenance of three digits that would otherwise
// have no story.
// WHAT THE SURFACE IS, beyond its colour: a name for the history, and the two numbers that
// separate felt from plated steel. All optional — a colour sampled off a photograph is three
// digits and nothing more, and the server's defaults are what it has always been given.
export type Surface = {
  material?: string;
  roughness?: number;
  metallic?: number;
  // KEEP THE MESH'S OWN GRAIN, rather than painting the colour flat over it. Only a
  // reconstruction has any — it arrives with a photograph of the real wood wrapped onto its
  // UVs — and painting flat drops that picture, which is what turns a leg into a moulding of
  // one. Asked for here because it is a different claim about the part, not a better way of
  // making the same one: see glb.painted. A mesh with no texture ignores it.
  grain?: boolean;
  // GIVE THE SURFACE ITS FIBRE — felt, wool, foam. What roughness 1 cannot say: it takes the
  // highlight off the whole part evenly, where a fibre is the surface facing a different way
  // at every square millimetre. Sent from the material's own words, and honoured only on a
  // mesh unwrapped in millimetres — which the shaped ones are and a reconstruction is not.
  fibre?: boolean;
  // AND A SURFACE OVER THE FLAT COLOUR — a default material's four numbers, drawn as a measured
  // material's are (see default_material_textures). Ignored where the grain is kept.
  texture?: { contrast: number; chroma: number; anisotropy: number; periodMm: number };
};

export async function colourJob(
  jobId: number, rgb: [number, number, number], from?: number, fromUrl?: string,
  surface?: Surface,
): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(
    `/api/notice/3d/jobs/${jobId}/colour`,
    // WHICH PICTURE SAID SO: a Parts-DB photograph by id, or a listing photograph by URL.
    // Three digits with no story is what provenance is for, and the two sources are not
    // interchangeable — one is the real object and the other is a generator's idea of it.
    //
    // A named material is its own provenance: nothing sampled it, somebody chose it.
    { color: rgb, from, fromUrl, ...(surface || {}) },
  );
  return data.job;
}

// WHICH WAY ROUND THE MESH ACTUALLY GOES, decided by looking at the assembled product.
//
// `photos` are the notice's own pictures of the finished thing, each with the side of the
// product it was classified as showing in Phase 1 — which is the half the model cannot get
// from the picture alone as reliably as it can be told. Order matters: the prompt numbers
// them and says what each one is.
//
// A MESH ALREADY THE RIGHT WAY ROUND COMES BACK UNTURNED, and says so. That is the expected
// answer for most parts, and it is not a failure to report as one.
// The same six words the rest of the app turns parts by — see faces.ts, which owns them and
// says which frame they are named in. Aliased rather than redeclared: this file is where the
// wire's vocabulary is documented, and a second list of the same strings is a second thing to
// keep in step with the server's FACES.
export type OrientFace = Face;

// WHAT THE MODEL ACTUALLY SAID: one of the six lettered views, and the side of the product
// it shows. This is the answer in the only vocabulary the model had — it was shown A to F
// and never told what the file calls them — and it is what the badge prints.
//
// AND WHETHER THE APPLIED TURN AGREES WITH IT. The call is asked about all six views
// independently now, so the six can contradict each other: the turn is the one that explains
// the most of them (see mesh_orient.fit_views), `applied` is what it makes of THIS view, and
// `fits` is whether that is what was said. Both absent on an answer recorded while the
// question asked for two perpendicular faces, which could not disagree with itself — so
// `fits === false` is the test, never `!fits`.
export type OrientView = {
  view: string;
  shows: OrientFace;
  applied?: OrientFace;
  fits?: boolean;
};

export type OrientAnswer = {
  // The answer resolved against the names the file ALREADY carried: "the side it labels
  // `left` is really the front". Derived from `views` and from the old labelling together,
  // so it is a comparison rather than a result — it is what the edit's history line is
  // written in, and it is deliberately not what the reasoning badge shows.
  front: OrientFace;
  top: OrientFace;
  // Absent on turns recorded before the answer was kept in its own words.
  views?: OrientView[];
  // WHETHER THE SIX ANSWERS AGREED WITH EACH OTHER, and whether they named a turn at all.
  //
  // `coherent` is all six answered and all six honoured by the turn that was applied.
  // `settled` is weaker and matters more: false means several turns explained the answers
  // equally well, so NOTHING was applied and the mesh is where it was. `turns` is how many —
  // which is the only thing a page can honestly say about an answer of that shape.
  //
  // All three absent on turns recorded before the question asked for six views. Read as
  // "settled", which they were: two perpendicular faces are a rotation by construction.
  coherent?: boolean;
  settled?: boolean;
  turns?: number;
  reason: string;
  // What it actually replied, whole. The reasoning is the model's account of itself; this
  // is what it said, and when a run comes back with a face nobody expected the difference
  // between the two is where the answer is.
  raw?: string;
  model?: string;
  // WHERE THE WAIT WENT, in seconds: the six renders the browser made, then the call.
  // Measured because this press was ninety seconds once and "is it the pictures or is it the
  // model" was otherwise unanswerable from outside — which is how a prompt gets shortened to
  // fix a bottleneck that was never in the prompt. The first number is taken in the page and
  // sent with the question; the second is the server's own.
  renderSeconds?: number;
  modelSeconds?: number;
};

// One photograph of the finished product that goes with an orientation question: which
// stored picture it is, the gallery URL it is filed under (which is what a part's crops are
// keyed by), and the side of the product it was classified as showing.
export type ProductShot = { id: number; url: string; view: string };

// And what actually goes up: the picture with this part outlined on it, as a data URL. The
// masks are burnt in by the page — see maskedShot — so what is sent is what the badge shows,
// down to which of them somebody has struck through.
export type MaskedShot = { dataUrl: string; view: string; masked: boolean };

// WHAT THE PART IS, which decides which orientation question gets asked about it — the twin
// of mesh_orient.KINDS on the other side of the wire.
//
// A component belongs to one product and its front is that product's front, which can only be
// found by matching the mesh against photographs of the assembled thing. Hardware and tools
// belong to nothing: the same screw goes into every wardrobe ever built, so there is nothing
// to match and the question is asked about the object itself — which side it rests on, which
// side does the work. See mesh_orient, which holds both prompts.
export type OrientKind = 'component' | 'hardware' | 'tool';

// `views` are the six pictures of the mesh, made in the browser — see meshAxisViews, which
// argues why they are not made on the server any more and why they are not shot through the
// viewer's own scene. They go up with the question because that is the only place they
// exist: nothing keeps them.
//
// `photos` is empty for hardware and tools, and the server drops them even if it is not: the
// prompt those get does not mention photographs, so one attached is a picture the model has
// to account for with nothing telling it what the picture is.
export async function orientJob(
  jobId: number, photos: MaskedShot[],
  views: { name: string; dataUrl: string }[], renderSeconds: number,
  kind: OrientKind = 'component',
): Promise<{ job: Job3D; orient: OrientAnswer; turned: boolean }> {
  const { data } = await axios.post<{ job: Job3D; orient: OrientAnswer; turned: boolean }>(
    `/api/notice/3d/jobs/${jobId}/orient`, { photos, views, renderSeconds, kind },
  );
  return data;
}

// AND THE ANSWER GIVEN BY HAND: two of the mesh's own faces, and the side of the product each
// one really is. The same shape of answer the model gives — and the same function derives the
// rotation from it — because "the side it calls the top is really the back" is the thing a
// person looking at the part knows, where an axis and a right angle is a thing they would have
// to work out.
//
// TWO PAIRS, AT RIGHT ANGLES ON BOTH SIDES OF THE ARROW. The dialog does not offer anything
// else and the server refuses it anyway: two faces of one axis describe no rotation, and one
// face cannot be two sides of the product. Two that pass are exactly one of the 24 axis-aligned
// orientations, and the other four faces fall out of it.
//
// The same edit the model's answer writes, replayed and undone by the same code. What tells
// them apart in the history is that this one was somebody's hand.
export async function rotateJob(
  jobId: number, faces: { from: OrientFace; to: OrientFace }[],
): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(
    `/api/notice/3d/jobs/${jobId}/rotate`, { faces },
  );
  return data.job;
}

// WHAT THAT BUTTON WOULD SEND, for the badge beside it. Built by the same function on the
// server that builds the real one — see PromptBadge, which is where this is shown and why
// it is fetched on hover and once.
//
// THE WORDS AND THE PRODUCT PHOTOGRAPHS ONLY, which is why it is instant. The six pictures
// of the mesh cost a cold Blender and the text does not need them: what the prompt says
// about them is their order, and the order is a constant on both sides. They come from
// orientViews, behind this.
export async function orientPrompt(
  jobId: number, photos: { view: string; masked: boolean }[],
  kind: OrientKind = 'component',
): Promise<{ prompt: string; labels: string[]; model: string }> {
  const { data } = await axios.post<{
    prompt?: string; labels?: string[]; model?: string;
  }>(`/api/notice/3d/jobs/${jobId}/orient`, { photos, preview: true, kind });
  return { prompt: data.prompt || '', labels: data.labels || [], model: data.model || '' };
}


// AND THE ONE PRESS THAT MAKES A SITTING'S WORK THE FILE'S. The picture the corrections
// arrived at, written into the .glb as ONE edit on the mesh's own undo stack — which is what
// makes the whole sitting safe to try, since the way back is a single press.
//
// `faces` and `marks` are how many of each went into it, for the rail to name the entry by.
export async function setPicture(
  jobId: number, index: number, image: Blob, faces: number, marks: number,
): Promise<Job3D> {
  const body = new FormData();
  body.append('image', image, 'texture.png');
  body.append('index', String(index));
  body.append('faces', String(faces));
  body.append('marks', String(marks));
  const { data } = await axios.post<{ job: Job3D }>(
    `/api/notice/3d/jobs/${jobId}/picture`, body,
  );
  return data.job;
}

// The two hand-written repairs a mesh could be given, still named on the edits they left.
export type RepairOp = 'fill_end_slot' | 'cut_drive_recess';

// A HOLE, BORED INTO THIS MESH, in place and undoable like every other edit.
//
// WHAT A RECONSTRUCTION IS ALWAYS MISSING. A photograph of a rail shows two dark discs on its
// end; the reconstruction reads them as shading and hands back a solid rail, and every phase
// after that works on a part that cannot take the screw the manual puts through it.
//
// SAID AS A PLACE ON A FACE, which is how this app says every place on a part: one of the six
// words the mesh's own box is labelled with, and a point on that face in millimetres from its
// bottom-left corner seen from outside (see partFaces, where the convention lives). The same
// answer Phase 5 asks a model for when two parts meet — a hole is that answer about one part.
//
// `mmPerUnit` is the scale the viewer draws this mesh by: a reconstruction's file units mean
// nothing until a measurement anchors them, and a hole asked for in millimetres has to be
// bored with the same anchor everything else on screen is measured with.
export async function drillJob(jobId: number, hole: {
  face: string;
  u: number;
  v: number;
  diameter: number;
  // Absent on a through hole, which is as deep as the part is thick.
  depth?: number | null;
  through: boolean;
  // WHAT SHAPE — 'plain', 'flat' or 'countersunk'. Absent is 'plain', which is the straight
  // bore this route made before there were three of them, so an old caller asks for what it
  // has always got. See holeStyleOf, which works it out from what the hole takes.
  style?: string;
  // A SUNK HEAD'S COUNTERBORE: how far below the face the head sits, and how wide the head is.
  sunk?: number;
  head?: number;
  mmPerUnit: number;
}): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(
    `/api/notice/3d/jobs/${jobId}/drill`, hole,
  );
  return data.job;
}

// SEVERAL BORES IN ONE BLENDER RUN — at most eight a call (see notice_3d.MAX_DRILL_BATCH).
// Each hole's outcome comes back in the order sent: cut, or why not.
export async function drillJobAll(jobId: number, holes: Omit<Parameters<typeof drillJob>[1], 'mmPerUnit'>[], mmPerUnit: number): Promise<{
  job: Job3D; holes: { ok: boolean; error?: string }[];
}> {
  const { data } = await axios.post<{ job: Job3D; holes: { ok: boolean; error?: string }[] }>(
    `/api/notice/3d/jobs/${jobId}/drill-all`, { holes, mmPerUnit },
  );
  return data;
}

// A WRITTEN transformation, applied to this mesh. "Shorten the legs by 20 mm" is a request
// to move exactly the numbers a repair may not, so this is verified only for still being a
// mesh, and it is undoable like everything else.
//
// `code` is Blender Python, written by the model for this request.
// It is read before it runs — see mesh_script.py — and again on the way in.
export async function runScript(
  jobId: number, code: string, name: string, proposalId?: number,
): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(
    `/api/notice/3d/jobs/${jobId}/script`, { code, name, proposalId },
  );
  return data.job;
}

// ONE CHANGE OUT OF THE HISTORY, leaving everything after it standing. The server goes back
// to the mesh as it stood before that entry and does the later ones again from what they
// recorded, so what comes back is the part without that one change and with every other.
//
// THIS IS ALSO THE UNDO. There was a /revert beside it that took the newest edit off and
// nothing else; dropping the newest is that operation with nothing to replay, so the two
// were one call wearing two names — and the button that was the only caller of the second
// was a second control for what the rail's newest dot already did.
//
// It can REFUSE, and then it says which entry stopped it. A texture sitting kept its marks
// and not the picture they made; a repair can be declined by its own verification. Neither
// can be made a second time, so neither can be replayed over the gap — see _REPLAYABLE.
export async function dropEdit(jobId: number, index: number): Promise<Job3D> {
  const { data } = await axios.delete<{ job: Job3D }>(
    `/api/notice/3d/jobs/${jobId}/edits/${index}`,
  );
  return data.job;
}

// AND SEVERAL AT ONCE — the ones a box dragged over the history caught. One call, one replay:
// back to before the earliest, then every later one that was not taken, done again.
// `keep`: an undo somebody pressed — a drilling it takes off stays on the history, crossed (see
// undoneOf). `forget`: the part drilled again from scratch, which drills every undone one again too.
export async function dropEdits(jobId: number, indices: number[], how: { keep?: boolean; forget?: boolean } = {}): Promise<Job3D> {
  const { data } = await axios.delete<{ job: Job3D }>(
    `/api/notice/3d/jobs/${jobId}/edits`, { data: { indices, ...how } },
  );
  return data.job;
}

// THE DRILLINGS UNDONE BY HAND (Louis 2026-10-29), each as it was and when it came off: drawn crossed
// on the history, made again by redoEdit.
export type UndoneEdit = MeshEdit & { undoneAt?: string };
export const undoneOf = (job: Job3D | null | undefined): UndoneEdit[] =>
  (job?.params?.undone as UndoneEdit[] | undefined) ?? [];
export async function redoEdit(jobId: number, k: number): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(`/api/notice/3d/jobs/${jobId}/undone/${k}/redo`);
  return data.job;
}

// A MESH SET ASIDE AS IT STANDS — file, history and undo copies — and put back, or let go: what
// Stop rolls a joint's parts back with (see JointsRun). File copies on the server, no Blender.
export async function holdMesh(jobId: number): Promise<void> {
  await axios.post(`/api/notice/3d/jobs/${jobId}/hold`);
}
export async function restoreMesh(jobId: number): Promise<Job3D> {
  const { data } = await axios.post<{ job: Job3D }>(`/api/notice/3d/jobs/${jobId}/hold/restore`);
  return data.job;
}
export async function releaseMesh(jobId: number): Promise<void> {
  await axios.delete(`/api/notice/3d/jobs/${jobId}/hold`);
}

// WHICH repair, chosen by a model that was shown the manual's drawing, three renders of
// the mesh as it stands, its measurements, and a sentence saying what is wrong.
//
// A PROPOSAL, not an edit. Nothing is cut until it is applied (runScript),
// and that call can still refuse. Two steps because the numbers are millimetres read off
// line art with a threaded hole on the other side of a wrong one: a model that proposes in
// front of somebody has visible misreadings, one that cuts and reports afterwards has a
// mesh in the Parts DB.
// A transformation the model WROTE for this request. Always written and never chosen: a
// model asked in words and handed a list to pick from will pick from the list — the answer
// to "shorten the legs" is not a drive recess.
//
// `op` is 'script' or null, and null means it had nothing to offer: the request was not
// about this mesh, or what it wrote was refused before anybody saw a button.
export type RepairProposal = {
  op: 'script' | null;
  // What the server would not pass on: a depth past the thread, a bar wider than the part.
  // Shown rather than silently corrected, because each one means the drawing was misread.
  // For a script: what will run, and three or four words naming what it does. Empty for a
  // listed repair, which is described by its own name and its millimetres.
  code: string;
  name: string;
  warnings: string[];
  reason: string;
  confidence: 'high' | 'medium' | 'low' | null;
};

// A proposal as the database keeps it: what was asked, what came back, and whether it was
// ever run. Everything RepairProposal holds, plus the three facts that make it a history.
export type MeshProposal = RepairProposal & {
  id: number;
  instruction: string;
  // When it was applied to the mesh, or null for one nobody ran — which is most of them,
  // and the half worth keeping most.
  appliedAt: string | null;
  createdAt: string | null;
  // How long the call took. Null on the proposals made before this was recorded.
  elapsedSeconds: number | null;
  // The proposal this was asked as a refinement OF — what makes the history a graph.
  // Null for one asked from the row of menus, which starts a new line.
  basedOnId: number | null;
  // THE RENDERS THE MODEL WAS SHOWN, by id. Kept now rather than held in the answer: the
  // reasoning above is written ABOUT these pictures — which end is short, where the thread
  // stops — and the mesh they are of has usually been edited since, so a proposal without
  // them is an argument about something nobody can look at any more.
  //
  // Empty on every proposal made before they were kept. That is a real state and not a
  // failure: the card shows what it has.
  views: { id: number; name: string }[];
};

// One render's bytes. Immutable, and served with a year's cache — a render is of a mesh at
// one moment and is never rewritten, so its id names those exact pixels for good.
export const proposalViewSrc = (viewId: number) =>
  apiUrl(`/api/notice/3d/proposal-view/${viewId}`);

// With the average one has taken ACROSS EVERY MESH — what the clock counts up to while a
// proposal is being written. Null until something has been timed.
export async function fetchProposals(
  jobId: number,
): Promise<{ proposals: MeshProposal[]; averageSeconds: number | null }> {
  const { data } = await axios.get<{
    proposals: MeshProposal[]; averageSeconds: number | null;
  }>(`/api/notice/3d/jobs/${jobId}/proposals`);
  return { proposals: data.proposals || [], averageSeconds: data.averageSeconds ?? null };
}

export async function deleteProposal(id: number): Promise<void> {
  await axios.delete(`/api/notice/3d/proposals/${id}`);
}

export type RepairAdvice = {
  proposal: MeshProposal;
  // Whether the manual actually had a drawing of this part to compare against. Without
  // one the proposal rests on the measurements and the sentence alone, and saying so is
  // the difference between a judgement and a guess.
  sawDrawing: boolean;
};

// `pictures` is WHAT to show it, picked in the ask modal: the part's drawings, and for a
// component the photographs of the product it belongs to. Split by table on the way out,
// because that is how the server has to fetch them.
//
// Sent on every call, empty included: an empty selection means "judge from the mesh
// alone", and the server can only tell that from a missing field if the page always says.
// WHAT A PROPOSAL WOULD BE SHOWN, without asking for one. The same renders, measurements
// and prompt the next adviseRepair call will use, assembled by the same code on the server
// — a preview built separately would agree with the call today and drift from it tomorrow.
//
// The sentence is left as `slot` in the prompt, because it is still being typed while this
// is read. Costs a Blender render and no model call.
export type AdviceContext = {
  prompt: string;
  // The token standing in for the instruction, for the page to substitute live.
  slot: string;
  views: { name: 'min' | 'max' | 'oblique'; dataUrl: string }[];
  inspect: Record<string, unknown>;
};

export async function fetchAdviceContext(
  jobId: number, pictures: PictureRef[], basedOnId?: number,
): Promise<AdviceContext> {
  const ids = (source: PictureSource) =>
    pictures.filter((p) => p.source === source).map((p) => p.imageId);
  const { data } = await axios.post<AdviceContext>(
    `/api/notice/3d/jobs/${jobId}/context`,
    { schematicIds: ids('schematic'), listingIds: ids('listing'), basedOnId },
  );
  return data;
}

// `basedOnId` names a proposal this request REFINES — pressed on that proposal rather
// than guessed from the sentence. Absent for a fresh change, which is the other button.
export async function adviseRepair(
  jobId: number, instruction: string, pictures: PictureRef[], basedOnId?: number,
): Promise<RepairAdvice> {
  const ids = (source: PictureSource) =>
    pictures.filter((p) => p.source === source).map((p) => p.imageId);
  const { data } = await axios.post<{
    proposal: {
      id: number;
      instruction: string;
      appliedAt: string | null;
      createdAt: string | null;
      elapsedSeconds: number | null;
      basedOnId: number | null;
      op: string | null;
      code?: string;
      name?: string;
      warnings: string[];
      reason: string;
      confidence: 'high' | 'medium' | 'low' | null;
      views?: { id: number; name: string }[];
    };
    sawDrawing: boolean;
  }>(`/api/notice/3d/jobs/${jobId}/advise`, {
    instruction,
    schematicIds: ids('schematic'),
    listingIds: ids('listing'),
    basedOnId,
  });
  const p = data.proposal;
  return {
    proposal: {
      id: p.id,
      instruction: p.instruction,
      appliedAt: p.appliedAt,
      createdAt: p.createdAt,
      elapsedSeconds: p.elapsedSeconds,
      basedOnId: p.basedOnId ?? null,
      op: p.op === 'script' ? 'script' : null,
      code: p.code || '',
      name: p.name || '',
      warnings: p.warnings || [],
      reason: p.reason || '',
      confidence: p.confidence,
      views: p.views || [],
    },
    sawDrawing: data.sawDrawing,
  };
}

// The complaints a refused repair came back with, out of an axios error.
export const repairComplaints = (e: unknown): string[] => {
  const d = axios.isAxiosError(e) ? (e.response?.data as { complaints?: string[] }) : null;
  return Array.isArray(d?.complaints) ? d.complaints : [];
};

// THE TWO WAYS A PART GOT A MESH BEFORE EVERY MODEL WAS GENERATED: a supplier's CAD file
// converted server-side, and a part turned from its measurements. Neither is made any more;
// the runs they left are still runs — viewed, kept and deleted like any other — and are read
// at true scale (see isTrueScale).
export const CAD_MODEL = 'cad';
export const LATHE_MODEL = 'lathe';

// A mesh that IS the part, in millimetres, with nothing to anchor it to. A converted STEP
// file because the supplier drew it that way; a turned one because the numbers it was
// turned from were millimetres. Neither is fitted to a drawing — see meshScene.
//
// AND A BAKED SUB-STEP OUTPUT: exported out of the assembly's millimetres into a glTF's metres
// (see AssemblyView.bake), so it is the piece at the size its parts were drawn at.
export const isTrueScale = (job: { model: string; source?: RunSource } | null | undefined) =>
  job?.model === CAD_MODEL || job?.model === LATHE_MODEL || job?.source === 'substep';

// A MESH'S OWN SIZE, in the file's own units and this app's axes. Null where it could not be
// measured, which is a mesh nobody can place either.
//
// CACHED FOREVER, per job, because it is a fact about bytes that never change: a job's GLB is
// overwritten by an edit, and an edit makes a NEW job (see save_edited_mesh). Phase 5 asks for
// this once per part while building a prompt, and it builds one on every hover of the badge.
// AND WHERE THE BOX IS, in the same units and axes: its two corners. What a piece is placed FROM
// — it stands off its file's origin, and its middle is what the scene centres it on.
export type Bounds = { min: [number, number, number]; max: [number, number, number] };
const measures = new Map<string, Promise<{ extents: Extent | null; box: Bounds | null }>>();

// `stamp`: the mesh's version (meshStamp), where the caller has it — a drilled or rescaled mesh is
// measured again rather than answered from before the change.
const measureOf = (jobId: number, stamp?: number) => {
  const at = stamp == null ? String(jobId) : `${jobId}@${stamp}`;
  const had = measures.get(at);
  if (had) return had;
  const asked = axios
    .get<{ extents?: Extent; box?: Bounds }>(`/api/notice/3d/jobs/${jobId}/extents`)
    .then(({ data }) => ({ extents: data.extents ?? null, box: data.box ?? null }))
    // A part whose mesh will not measure says less about itself; it does not fail the call
    // it was being described for. Left in the cache as a null so it is not asked again.
    .catch(() => ({ extents: null, box: null }));
  measures.set(at, asked);
  return asked;
};

export const jobExtents = (jobId: number, stamp?: number): Promise<Extent | null> =>
  measureOf(jobId, stamp).then((m) => m.extents);

export const jobBounds = (jobId: number, stamp?: number): Promise<Bounds | null> =>
  measureOf(jobId, stamp).then((m) => m.box);

// The Parts DB's meshes, newest first — every part's, or one part's.
export async function fetchCatalogModels(itemId?: number): Promise<Job3D[]> {
  const { data } = await axios.get<{ models: Job3D[] }>('/api/notice/3d/catalog', {
    params: itemId === undefined ? undefined : { itemId },
  });
  return data.models || [];
}

// WHETHER AN ERROR IS ALREADY ON SCREEN as a failed run's row. A run that fails is recorded, and
// its row under the button says why with a bin beside it — the same sentence again in red over the
// page is noise. An error no row carries — a refusal before any run, a connection lost — is still
// said.
export const errorOnARow = (error: string, jobs: Job3D[]) =>
  jobs.some((j) => !j.ready && j.error === error);

// What a failed request actually said. The backend answers { error } on every failure
// path it owns; anything else (a 502 from nginx, a dropped connection) has no body to
// read, and `fallback` is what the page shows instead of "undefined".
export function falError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string; refused?: string } | undefined;
    // A WRITE THE DEMO REFUSED SAYS NOTHING — see _WRITE_REFUSAL. Moving things about in a copy
    // that keeps nothing is what it is for, and '' is what every caller here draws as no error.
    if (IS_DEMO && data?.refused === 'write') return '';
    if (data?.error) return data.error;
  }
  return fallback;
}
